import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Card,
  CardBody,
  CardTitle,
  CardText,
  Row,
  Col,
  FormGroup,
  Label,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Spinner,
  Badge,
  Alert
} from 'reactstrap';
import Switch from 'react-switch';
import BZAPIService from '../../../services/BZAPIService';
import { toast } from 'react-toastify';
import Loader from '../../common/Loader';

const DEFAULT_DISCLAIMER_TEXT =
  'I understand that AI-generated content may not always be accurate and that I should review it before use.';

const AITab = ({
  vendorId,
  isReadOnly = false,
  onAiEnabledChange,
  aiTogglesPriorityType = false,
  priorityMaps = []
}) => {
  const [vendorSettings, setVendorSettings] = useState(null);
  const [globalAi, setGlobalAi] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [pendingAiEnabled, setPendingAiEnabled] = useState(null);
  const [resetModalOpen, setResetModalOpen] = useState(false);
  const [pendingResetEnabled, setPendingResetEnabled] = useState(null);

  const loadAiSettings = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await BZAPIService.getVendorAiSetting(vendorId);
      if (response) {
        setVendorSettings(response.vendor);
        setGlobalAi(response.globalAi);
      }
    } catch (error) {
      console.error('Error loading AI settings:', error);
      toast.error('Failed to load AI settings');
    } finally {
      setIsLoading(false);
    }
  }, [vendorId]);

  useEffect(() => {
    loadAiSettings();
  }, [loadAiSettings]);

  const handleToggle = () => {
    if (!vendorSettings) return;

    const newAiEnabled = !vendorSettings.aiEnabled;

    // If trying to enable AI and disclaimer not accepted, show disclaimer modal
    if (newAiEnabled && !vendorSettings.disclaimerAccepted) {
      setPendingAiEnabled(true);
      setModalOpen(true);
      return;
    }

    // If trying to disable AI and it's required, show error
    if (!newAiEnabled && vendorSettings.aiRequired && vendorSettings.disclaimerAccepted) {
      toast.error('AI is required for this vendor and cannot be disabled');
      return;
    }

    // Schema flag: AI mode change toggles the priority option set.
    // If maps already exist, get explicit consent before clearing them.
    if (aiTogglesPriorityType && priorityMaps.length > 0) {
      setPendingResetEnabled(newAiEnabled);
      setResetModalOpen(true);
      return;
    }

    // Otherwise, just toggle (callback to refresh dropdown filter).
    updateAiSetting({ aiEnabled: newAiEnabled });
  };

  const handleSignDisclaimer = () => {
    setPendingAiEnabled(true);
    setModalOpen(true);
  };

  const confirmDisclaimer = async () => {
    try {
      setIsSaving(true);
      const newAiEnabled = pendingAiEnabled !== null ? pendingAiEnabled : true;
      await BZAPIService.updateVendorAiSetting(vendorId, {
        aiEnabled: newAiEnabled,
        disclaimerAccepted: true
      });
      toast.success('AI settings updated successfully');
      setModalOpen(false);
      setPendingAiEnabled(null);
      await loadAiSettings();
      const shouldReset = aiTogglesPriorityType && priorityMaps.length > 0;
      if (onAiEnabledChange) {
        onAiEnabledChange(newAiEnabled && globalAi?.aiEnabled, { resetPriorityMaps: shouldReset });
      }
      if (shouldReset) {
        toast.info('Priority mappings cleared. Please remap.');
      }
    } catch (error) {
      console.error('Error updating AI settings:', error);
      toast.error('Failed to update AI settings');
    } finally {
      setIsSaving(false);
    }
  };

  const cancelDisclaimer = () => {
    setModalOpen(false);
    setPendingAiEnabled(null);
  };

  const confirmReset = async () => {
    const newAiEnabled = pendingResetEnabled;
    await updateAiSetting({ aiEnabled: newAiEnabled }, { resetPriorityMaps: true });
    setResetModalOpen(false);
    setPendingResetEnabled(null);
  };

  const cancelReset = () => {
    setResetModalOpen(false);
    setPendingResetEnabled(null);
  };

  const updateAiSetting = async (data, { resetPriorityMaps = false } = {}) => {
    try {
      setIsSaving(true);
      await BZAPIService.updateVendorAiSetting(vendorId, data);
      toast.success('AI settings updated successfully');
      await loadAiSettings();
      if (onAiEnabledChange && data.aiEnabled !== undefined) {
        onAiEnabledChange(data.aiEnabled && globalAi?.aiEnabled, { resetPriorityMaps });
      }
      if (resetPriorityMaps) {
        toast.info('Priority mappings cleared. Please remap.');
      }
    } catch (error) {
      console.error('Error updating AI settings:', error);
      toast.error('Failed to update AI settings');
    } finally {
      setIsSaving(false);
    }
  };

  const formatDate = dateString => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const isControlsDisabled = !globalAi?.aiEnabled || isReadOnly || isSaving;

  if (isLoading) {
    return <Loader text="Loading AI settings..." />;
  }

  if (!vendorSettings) {
    return (
      <div style={{ minWidth: '100%' }}>
        <Alert color="warning" fade={false}>
          Unable to load AI settings for this vendor.
        </Alert>
      </div>
    );
  }

  return (
    <div style={{ minWidth: '100%' }}>
      {/* AI Configuration Card */}
      <Card bg={'light'} text={'dark'} className="mb-4">
        <CardBody>
          {/* Global AI Disabled Warning */}
          {!globalAi?.aiEnabled && (
            <Alert color="warning" className="mb-3">
              <strong>Global AI is disabled.</strong> Enable Global AI in Admin Settings to configure AI usage for this
              vendor.
            </Alert>
          )}
          <CardTitle>AI Configuration</CardTitle>
          <CardText>Configure AI-powered features for this service</CardText>

          {/* Info Section */}
          <Row className="mb-4">
            <Col lg="6">
              <div className="mb-3">
                <Label className="fw-bold mb-1">Global AI Status</Label>
                <div>
                  <Badge color={globalAi?.aiEnabled ? 'success' : 'secondary'} pill>
                    {globalAi?.aiEnabled ? 'Enabled' : 'Disabled'}
                  </Badge>
                </div>
              </div>

              <div className="mb-3">
                <Label className="fw-bold mb-1">AI Required</Label>
                <div>
                  <Badge color={vendorSettings.aiRequired ? 'warning' : 'info'} pill>
                    {vendorSettings.aiRequired ? 'Required' : 'Optional'}
                  </Badge>
                </div>
              </div>

              {vendorSettings.aiApplicationDescription && (
                <div className="mb-3">
                  <Label className="fw-bold mb-1">AI Usage</Label>
                  <p className="text-muted mb-0">{vendorSettings.aiApplicationDescription}</p>
                </div>
              )}
            </Col>

            <Col lg="6">
              <div className="mb-3">
                <Label className="fw-bold mb-1">Disclaimer Status</Label>
                <div>
                  <Badge color={vendorSettings.disclaimerAccepted ? 'success' : 'secondary'} pill>
                    {vendorSettings.disclaimerAccepted ? 'Accepted' : 'Not Accepted'}
                  </Badge>
                </div>
              </div>

              {vendorSettings.disclaimerAccepted && (
                <>
                  {vendorSettings.disclaimerAcceptedBy && (
                    <div className="mb-2">
                      <Label className="fw-bold mb-1">Accepted By</Label>
                      <p className="text-muted mb-0">{vendorSettings.disclaimerAcceptedBy}</p>
                    </div>
                  )}
                  {vendorSettings.disclaimerAcceptedAt && (
                    <div className="mb-2">
                      <Label className="fw-bold mb-1">Accepted At</Label>
                      <p className="text-muted mb-0">{formatDate(vendorSettings.disclaimerAcceptedAt)}</p>
                    </div>
                  )}
                </>
              )}
            </Col>
          </Row>

          <hr />

          {/* Controls Section */}
          <Row className="mb-3">
            <Col lg="12">
              <FormGroup>
                <div className="d-flex align-items-center mb-3">
                  <Switch
                    onChange={handleToggle}
                    checked={!!vendorSettings.aiEnabled}
                    onColor="#253746"
                    uncheckedIcon={false}
                    checkedIcon={false}
                    height={20}
                    width={40}
                    handleDiameter={16}
                    className="me-2"
                    data-testid="ai-toggle"
                    disabled={isControlsDisabled}
                  />
                  <Label className="mb-0">Enable AI for this vendor</Label>
                </div>

                {vendorSettings.aiRequired && <span className="text-danger">* AI is required for this vendor</span>}

                {!vendorSettings.disclaimerAccepted && (
                  <div className="mt-3">
                    <Button
                      color="primary"
                      onClick={handleSignDisclaimer}
                      disabled={isControlsDisabled}
                      data-testid="sign-disclaimer-btn"
                    >
                      Sign Disclaimer to Enable AI
                    </Button>
                  </div>
                )}
              </FormGroup>
            </Col>
          </Row>
        </CardBody>
      </Card>

      {/* Disclaimer Modal */}
      <Modal isOpen={modalOpen} toggle={cancelDisclaimer}>
        <ModalHeader toggle={cancelDisclaimer}>AI Disclaimer Agreement</ModalHeader>
        <ModalBody>
          <p>{vendorSettings.disclaimerText || DEFAULT_DISCLAIMER_TEXT}</p>
          {aiTogglesPriorityType && priorityMaps.length > 0 && (
            <p className="mt-3" style={{ color: '#c24729' }}>
              Enabling AI will clear your existing priority mapping. After the disclaimer, set a new mapping using the
              AI priority options — it will be applied to every managed product of this vendor on the next service run.
            </p>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={cancelDisclaimer} disabled={isSaving}>
            Cancel
          </Button>
          <Button color="primary" onClick={confirmDisclaimer} disabled={isSaving}>
            {isSaving ? <Spinner size="sm" className="me-2" /> : null} I Agree
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={resetModalOpen} toggle={cancelReset}>
        <ModalHeader toggle={cancelReset}>Clear Priority Mappings?</ModalHeader>
        <ModalBody>
          <p>
            Switching the AI mode will change the available priority options. Your existing priority mapping will be
            cleared so you can set new ones. The new mapping will then be applied to every managed product of this
            vendor on the next service run.
          </p>
          <p>Continue?</p>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={cancelReset} disabled={isSaving}>
            Cancel
          </Button>
          <Button color="primary" onClick={confirmReset} disabled={isSaving}>
            {isSaving ? <Spinner size="sm" className="me-2" /> : null} Continue
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

AITab.propTypes = {
  vendorId: PropTypes.string.isRequired,
  isReadOnly: PropTypes.bool,
  onAiEnabledChange: PropTypes.func,
  aiTogglesPriorityType: PropTypes.bool,
  priorityMaps: PropTypes.array
};

export default AITab;
